import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Alert,
  AlertDescription,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { useWalletStore } from '../stores/useWalletStore';
import { toast } from 'sonner';
import {
  CLIENT_FACILITY_REQUIREMENTS,
  CLIENT_MARGIN_ACCOUNTS_BY_FACILITY,
  CLIENT_FACILITIES,
  CLIENT_FACILITY_BORROWED,
  CLIENT_FACILITY_TVL,
  CLIENT_WALLET_ONCHAIN_IDS,
} from '../data/mockAdapter';

// ─── Compliance Types ───────────────────────────────────────────────────────────

interface Claim {
  type: string;
  issuer: string;
  value: string;
  issuedAt: string;
}

interface OnchainId {
  id: string;
  address: string;
  claims: Claim[];
  source: 'ascend' | 'external' | 'erc3643';
}

interface ComplianceRequirement {
  type: 'external_kyc_claim' | 'internal_claim';
  claimType: string;
  description: string;
}

interface ComplianceCheckResult {
  hasAccess: boolean;
  requirementsMet: string[];
  requirementsNeeded: ComplianceRequirement[];
  matchedOnchainId?: string;
}

type ComplianceStepStatus = 'checking' | 'pass' | 'fail' | 'not-required';

interface ComplianceSteps {
  onchainId: { status: ComplianceStepStatus; message: string };
  claim: { status: ComplianceStepStatus; message: string };
  policy: { status: ComplianceStepStatus; message: string };
}

// ─── Facility Types ─────────────────────────────────────────────────────────────

type FacilityStatus = 'active' | 'paused';

interface RwaToken {
  symbol: string;
  address: string;
  requiredClaim?: string;
}

interface Facility {
  id: string;
  name: string;
  settlementTokens: string[];
  collateralTokens: string[];
  interestRate: string;
  maxLTV: string;
  liquidationThreshold: string;
  utilization: string;
  status: FacilityStatus;
  region: string;
  category: string;
  rwaTokens?: RwaToken[];
}

interface MarginAccountPosition {
  accountId: string;
  deposited: number;
  borrowed: number;
  healthFactor: number;
  availableCredit: number;
  interestRatePct: number;
  interestAccruedUsd: number;
  interestAccrued: string;
  liquidationThresholdPct: number;
  status: string;
}

const WALLET_ONCHAIN_IDS: Record<string, OnchainId[]> = {
  default: CLIENT_WALLET_ONCHAIN_IDS.default.map((entry) => ({
    id: entry.id,
    address: entry.address,
    source: entry.source,
    claims: entry.claims.map((claim) => ({
      type: claim.type,
      issuer: claim.issuer,
      value: claim.value,
      issuedAt: claim.issuedAt,
    })),
  })),
  no_ids: [],
  no_claims: CLIENT_WALLET_ONCHAIN_IDS.no_claims.map((entry) => ({
    id: entry.id,
    address: entry.address,
    source: entry.source,
    claims: [],
  })),
};

const FACILITY_REQUIREMENTS: Record<string, ComplianceRequirement[]> = CLIENT_FACILITY_REQUIREMENTS;

function tokenToAddress(facilityId: string, token: string): string {
  const input = `${facilityId}:${token}`;
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  const hex = hash.toString(16).padStart(8, '0');
  return `0x${hex.repeat(5).slice(0, 40)}`;
}

const FACILITIES: Facility[] = CLIENT_FACILITIES.map((facility) => ({
  id: facility.id,
  name: facility.name,
  settlementTokens: facility.settlementTokens,
  collateralTokens: facility.collateralTokens,
  interestRate: facility.interestRate,
  maxLTV: facility.maxLTV,
  liquidationThreshold: facility.liquidationThreshold,
  utilization: facility.utilization,
  status: facility.status,
  region: facility.region,
  category: facility.category,
  rwaTokens: facility.collateralTokens.map((token: string, index: number) => ({
    symbol: token,
    address: tokenToAddress(facility.id, token),
    requiredClaim: FACILITY_REQUIREMENTS[facility.id]?.[index]?.claimType,
  })),
}));

const FACILITY_TVL: Record<string, string> = CLIENT_FACILITY_TVL;
const FACILITY_BORROWED: Record<string, string> = CLIENT_FACILITY_BORROWED;

const MARGIN_ACCOUNTS: Record<string, MarginAccountPosition[]> = CLIENT_MARGIN_ACCOUNTS_BY_FACILITY;

const STATUS_CONFIG: Record<FacilityStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', className: 'bg-yellow-100 text-yellow-700' },
};

// ─── Compliance Check Logic ─────────────────────────────────────────────────────

function checkCompliance(facilityId: string, onchainIds: OnchainId[]): ComplianceCheckResult {
  const requirements = FACILITY_REQUIREMENTS[facilityId] ?? [];

  if (requirements.length === 0) {
    return { hasAccess: true, requirementsMet: [], requirementsNeeded: [], matchedOnchainId: onchainIds[0]?.id };
  }

  const met: string[] = [];
  const needed: ComplianceRequirement[] = [];
  let matchedId: string | undefined;

  for (const req of requirements) {
    let found = false;
    for (const oid of onchainIds) {
      if (oid.claims.some((c) => c.type === req.claimType)) {
        found = true;
        if (!matchedId) matchedId = oid.id;
        break;
      }
    }
    if (found) met.push(req.claimType);
    else needed.push(req);
  }

  return { hasAccess: needed.length === 0, requirementsMet: met, requirementsNeeded: needed, matchedOnchainId: matchedId };
}

// ─── Demo Scenario Type ─────────────────────────────────────────────────────────

type DemoScenario = 'default' | 'no_ids' | 'no_claims';

// ─── Page ───────────────────────────────────────────────────────────────────────

export function CreditFacilityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isConnected } = useWalletStore();

  const facility = FACILITIES.find((v) => v.id === id) ?? FACILITIES[0];
  const accounts = isConnected ? (MARGIN_ACCOUNTS[facility.id] ?? []) : [];
  const status = STATUS_CONFIG[facility.status];
  const tvl = FACILITY_TVL[facility.id] ?? '$0';
  const borrowed = FACILITY_BORROWED[facility.id] ?? '$0';
  const utilizationNum = parseFloat(facility.utilization);
  const requirements = useMemo(() => FACILITY_REQUIREMENTS[facility.id] ?? [], [facility.id]);

  const [demoScenario, setDemoScenario] = useState<DemoScenario>('default');
  const [isDetecting, setIsDetecting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [detectedIds, setDetectedIds] = useState<OnchainId[]>([]);
  const [complianceResult, setComplianceResult] = useState<ComplianceCheckResult | null>(null);
  const [complianceSteps, setComplianceSteps] = useState<ComplianceSteps | null>(null);
  const [showComplianceDetail, setShowComplianceDetail] = useState(true);
  const [kycSubmitted, setKycSubmitted] = useState(false);
  const [claimRequested, setClaimRequested] = useState(false);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    terms: false,
    risk: false,
    fees: false,
  });

  function toggleSection(key: string) {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  useEffect(() => {
    if (!isConnected) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Defer all state updates to async ticks so the effect body itself stays
    // free of synchronous setState (per react-hooks/set-state-in-effect).
    const t0 = setTimeout(() => {
      if (cancelled) return;
      setIsDetecting(true);
      setComplianceResult(null);
      setComplianceSteps(null);
      setKycSubmitted(false);
      setClaimRequested(false);

      // Filter to just the Ascend-scoped OnchainID — per spec there is exactly
      // one Ascend OID per user; we don't surface or detect token-level OIDs.
      const allIds = WALLET_ONCHAIN_IDS[demoScenario] ?? [];
      const ascendOnly = allIds.filter((oid) => oid.source === 'ascend');
      const ids = ascendOnly.length > 0 ? ascendOnly.slice(0, 1) : allIds.slice(0, 1);

      const t1 = setTimeout(() => {
        if (cancelled) return;
        setDetectedIds(ids);
        setIsDetecting(false);

        if (ids.length === 0) {
          setComplianceSteps({
            onchainId: { status: 'fail', message: 'No Ascend OnchainID — create one' },
            claim: { status: 'fail', message: 'OnchainID required first' },
            policy: { status: 'fail', message: 'Facility access not granted' },
          });
          return;
        }

        setIsChecking(true);
        const t2 = setTimeout(() => {
          if (cancelled) return;
          const result = checkCompliance(facility.id, ids);
          setComplianceResult(result);

          setComplianceSteps({
            onchainId: {
              status: 'pass',
              message: 'Ascend OnchainID present',
            },
            claim: {
              status: result.requirementsNeeded.length === 0
                ? (requirements.length === 0 ? 'not-required' : 'pass')
                : 'fail',
              message: result.requirementsNeeded.length === 0
                ? (requirements.length === 0 ? 'No required claims' : 'All required claims present')
                : `Missing: ${result.requirementsNeeded.map((r) => r.claimType.replace(/_/g, ' ')).join(', ')}`,
            },
            policy: {
              status: result.hasAccess ? 'pass' : 'fail',
              message: result.hasAccess ? 'Runtime predicates evaluated at borrow time' : 'Facility access not granted',
            },
          });

          setIsChecking(false);
        }, 800);
        timers.push(t2);
      }, 1000);
      timers.push(t1);
    }, 0);
    timers.push(t0);

    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
    };
  }, [demoScenario, facility.id, isConnected, requirements]);

  const allChecksPassed = complianceSteps
    ? (['onchainId', 'claim', 'policy'] as const).every(
        (k) => complianceSteps[k].status === 'pass' || complianceSteps[k].status === 'not-required',
      )
    : false;

  const hasIssues = complianceSteps
    ? (['onchainId', 'claim', 'policy'] as const).some(
        (k) => complianceSteps[k].status === 'fail',
      )
    : false;

  const demoFacilityExamples = useMemo(() => {
    return ([0, 1, 2] as const)
      .map((requiredClaimsCount) => (
        FACILITIES.find((candidate) => (FACILITY_REQUIREMENTS[candidate.id]?.length ?? 0) === requiredClaimsCount)
      ))
      .filter((candidate): candidate is Facility => Boolean(candidate));
  }, []);

  return (
    <div className="flex gap-6">
      {/* Main Content */}
      <div className="min-w-0 flex-1 space-y-6 sm:space-y-8">
        {/* Back link */}
        <Link
          to="/credit-facilities"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Facilities
        </Link>

        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <Icon name="streamline:bank" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-xl sm:text-2xl font-bold">{facility.name}</h1>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
                {facility.region && (
                  <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{facility.region}</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                This facility defines which tokens are accepted and the lending terms. Create a margin account here to deposit collateral and borrow.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Settlement: {facility.settlementTokens.join(', ')} · Collateral: {facility.collateralTokens.join(', ')} · {facility.interestRate} APR
              </p>
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          <StatCard title="Interest Rate" value={facility.interestRate} subtitle="Annual rate" icon={<Icon name="streamline:discount-percent-circle" className="h-5 w-5 text-muted-foreground" />} />
          <StatCard title="Max LTV" value={facility.maxLTV} subtitle="Loan-to-value ratio" icon={<Icon name="streamline:discount-percent-circle" className="h-5 w-5 text-muted-foreground" />} />
          <StatCard title="Liquidation Threshold" value={facility.liquidationThreshold} subtitle="Minimum collateral ratio" icon={<Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-5 w-5 text-muted-foreground" />} />
          <StatCard title="Utilization" value={facility.utilization} subtitle="Pool utilization rate" icon={<Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-5 w-5 text-muted-foreground" />} />
        </div>

        {/* Compliance Pre-Check (connected only) */}
        {isConnected && (
          <CompliancePreCheck
            isDetecting={isDetecting}
            isChecking={isChecking}
            steps={complianceSteps}
            allPassed={allChecksPassed}
            hasIssues={hasIssues}
            showDetail={showComplianceDetail}
            onToggleDetail={() => setShowComplianceDetail((v) => !v)}
          />
        )}

        {/* OnchainID Detection */}
        {isConnected && (
          <OnchainIdSection
            ids={detectedIds}
            isDetecting={isDetecting}
            onCreateId={() => {
              const newId: OnchainId = {
                id: `oid-new-${Date.now()}`,
                address: `0x${Math.floor(Math.random() * 0xffffffffffff).toString(16).padStart(12, '0')}...${Math.floor(Math.random() * 0xffff).toString(16).padStart(4, '0')}`,
                claims: [],
                source: 'ascend',
              };
              setDetectedIds((prev) => [...prev, newId]);
              toast.success('OnchainID created on Ascend Identity Registry');
              setComplianceSteps((prev) => prev ? {
                ...prev,
                onchainId: { status: 'pass', message: 'Ascend OnchainID present' },
              } : prev);
            }}
          />
        )}

        {/* Compliance Requirements */}
        {requirements.length > 0 && (
          <ComplianceRequirementsCard
            requirements={requirements}
            result={complianceResult}
            isConnected={isConnected}
            facilityName={facility.name}
            kycSubmitted={kycSubmitted}
            claimRequested={claimRequested}
            onSubmitKyc={() => {
              setKycSubmitted(true);
              toast.success('KYC verification request submitted');
            }}
            onRequestClaim={() => {
              setClaimRequested(true);
              toast.success('Claim request submitted to facility administrator');
            }}
          />
        )}

        {/* No requirements */}
        {isConnected && requirements.length === 0 && complianceResult && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">No further verification required</p>
                  <p className="text-xs text-green-700 mt-1">
                    All compliance requirements met for <span className="font-semibold">{facility.name}</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Facility Parameters */}
        <Card>
          <CardHeader className="p-6">
            <CardTitle>Facility Parameters</CardTitle>
            <CardDescription>Accepted tokens, lending terms, and thresholds that apply to all margin accounts in this facility.</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <ParamItem icon={<Icon name="streamline:coins-stack" className="h-4 w-4 text-muted-foreground" />} label="Collateral Tokens" value={facility.collateralTokens.join(', ')} />
              <ParamItem icon={<Icon name="streamline:coins-stack" className="h-4 w-4 text-muted-foreground" />} label="Settlement Tokens" value={facility.settlementTokens.join(', ')} />
              <ParamItem icon={<Icon name="streamline:graph-bar-increase" className="h-4 w-4 text-muted-foreground" />} label="Total Value Locked" value={tvl} />
              <ParamItem icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-4 w-4 text-muted-foreground" />} label="Total Borrowed" value={borrowed} />
              <ParamItem icon={<Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-4 w-4 text-muted-foreground" />} label="Max LTV" value={facility.maxLTV} />
              <ParamItem icon={<Icon name="streamline:warning-triangle" className="h-4 w-4 text-muted-foreground" />} label="Liquidation Threshold" value={facility.liquidationThreshold} />
            </div>

            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Utilization</span>
                <span className="font-medium">{facility.utilization}</span>
              </div>
              <div className="h-3 w-full rounded-full bg-muted">
                <div
                  className={`h-3 rounded-full transition-all ${
                    utilizationNum >= 85 ? 'bg-red-500' : utilizationNum >= 60 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(utilizationNum, 100)}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Margin Accounts in this Facility */}
        {isConnected && accounts.length > 0 && (
          <Card>
            <CardHeader className="p-6">
              <CardTitle>Your Margin Accounts</CardTitle>
              <CardDescription>
                {accounts.length} margin account{accounts.length !== 1 ? 's' : ''} in this credit facility.
                Deposits, borrows, and repayments happen at the margin account level.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {accounts.map((acct) => (
                <div key={acct.accountId} onClick={() => navigate(`/margin-accounts/${acct.accountId}`)} className="cursor-pointer rounded-xl border border-border p-4 hover:bg-accent/30 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4 text-muted-foreground" />
                      <span className="font-mono text-sm font-semibold">{acct.accountId}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                        acct.status === 'healthy' || acct.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : acct.status === 'at-risk' || acct.status === 'warn'
                          ? 'bg-amber-100 text-amber-700'
                          : acct.status === 'frozen'
                          ? 'bg-blue-100 text-blue-700'
                          : acct.status === 'paused'
                          ? 'bg-orange-100 text-orange-700'
                          : acct.status === 'liquidated' || acct.status === 'default'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>{acct.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/deposit?facilityId=${facility.id}`)}>Deposit</Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/withdraw?accountId=${acct.accountId}`)}>Withdraw</Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/borrow?accountId=${acct.accountId}`)}>Borrow</Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/repay?accountId=${acct.accountId}`)}>Repay</Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div>
                      <span className="text-xs text-muted-foreground">Collateral Deposited</span>
                      <div className="mt-1 text-lg font-bold">${acct.deposited.toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Borrowed</span>
                      <div className="mt-1 text-lg font-bold">${acct.borrowed.toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Health Factor</span>
                      <div className={`mt-1 text-lg font-bold ${
                        acct.healthFactor >= 1.5 ? 'text-green-600' : acct.healthFactor >= 1.2 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {acct.healthFactor.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Available Credit</span>
                      <div className="mt-1 text-lg font-bold">${acct.availableCredit.toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Interest Rate</span>
                      <div className="mt-1 text-lg font-bold">{acct.interestRatePct.toFixed(1)}%</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Interest Accrued</span>
                      <div className="mt-1 text-lg font-bold">${acct.interestAccruedUsd.toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Liq. Threshold</span>
                      <div className="mt-1 text-lg font-bold">{acct.liquidationThresholdPct}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Create Margin Account CTA */}
        {isConnected && allChecksPassed && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex flex-col items-center gap-4 py-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Icon name="streamline:bank" className="h-7 w-7 text-primary" />
                </div>
                <div className="text-center">
                  <h3 className="font-semibold">
                    {accounts.length > 0 ? 'Create Another Margin Account' : 'Ready to Create a Margin Account'}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {accounts.length > 0
                      ? `You have ${accounts.length} margin account${accounts.length !== 1 ? 's' : ''} in this facility. You can create additional ones.`
                      : 'All compliance checks passed. Create a margin account to deposit collateral and start borrowing.'}
                  </p>
                </div>
                <Button onClick={() => navigate(`/margin-accounts/create?facilityId=${facility.id}`)}>
                  Create Margin Account
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Not connected CTA */}
        {!isConnected && (
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                  <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-7 w-7 text-muted-foreground" />
                </div>
                <div className="text-center">
                  <h3 className="font-semibold">Connect Wallet to Check Eligibility</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Connect your wallet to check if you meet the compliance requirements for this credit facility.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expandable sections */}
        <div className="space-y-3">
          <ExpandableSection
            title="Loan Terms & Conditions"
            description="Detailed terms governing loans issued from this facility."
            isOpen={expandedSections.terms}
            onToggle={() => toggleSection('terms')}
          >
            <div className="space-y-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Minimum Loan Amount</span><span className="font-medium">$1,000</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Maximum Loan Amount</span><span className="font-medium">$5,000,000</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Interest Payment</span><span className="font-medium">Accrued per block</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Repayment Schedule</span><span className="font-medium">Flexible (no fixed schedule)</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Early Repayment</span><span className="font-medium">No penalty</span></div>
            </div>
          </ExpandableSection>

          <ExpandableSection
            title="Risk Parameters"
            description="Risk management settings for this facility."
            isOpen={expandedSections.risk}
            onToggle={() => toggleSection('risk')}
          >
            <div className="space-y-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Oracle Provider</span><span className="font-medium">Chainlink</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Price Update Frequency</span><span className="font-medium">Every block</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Liquidation Penalty</span><span className="font-medium">5%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Reserve Factor</span><span className="font-medium">10%</span></div>
            </div>
          </ExpandableSection>

          <ExpandableSection
            title="Fee Structure"
            description="Fees associated with this facility."
            isOpen={expandedSections.fees}
            onToggle={() => toggleSection('fees')}
          >
            <div className="space-y-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Origination Fee</span><span className="font-medium">0.1%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Management Fee</span><span className="font-medium">0.25% annual</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Withdrawal Fee</span><span className="font-medium">None</span></div>
            </div>
          </ExpandableSection>
        </div>
      </div>

      {/* Demo Controls Sidebar (connected only) */}
      {isConnected && (
        <div className="hidden lg:block w-80 flex-shrink-0">
          <div className="sticky top-24">
            <Card className="border-dashed border-orange-300 bg-orange-50/30">
              <CardHeader className="p-6 pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Demo Controls</CardTitle>
                  <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 border border-orange-300">
                    Internal Use Only
                  </span>
                </div>
                <CardDescription className="text-xs">
                  Simulate different onchainID and compliance scenarios
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="space-y-4">
                  {/* Facility Scenarios */}
                  <div>
                    <p className="text-xs font-medium mb-2">Credit Facility Scenarios (0/1/2 req examples):</p>
                    <div className="flex flex-col gap-2">
                      {demoFacilityExamples.map((v) => {
                        const reqCount = FACILITY_REQUIREMENTS[v.id]?.length ?? 0;
                        return (
                          <Button
                            key={v.id}
                            size="sm"
                            variant={v.id === facility.id ? 'default' : 'outline'}
                            onClick={() => navigate(`/credit-facilities/${v.id}`)}
                            className="justify-start text-xs"
                          >
                            <Icon name="streamline:bank" className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                            <span className="truncate">{v.name}</span>
                            <span className="ml-auto text-[10px] opacity-70">
                              {reqCount} req{reqCount === 1 ? '' : 's'}
                            </span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  {/* OnchainID Scenario */}
                  <div>
                    <p className="text-xs font-medium mb-2">Wallet OnchainID Scenario:</p>
                    <div className="flex flex-col gap-1.5">
                      {([
                        { key: 'default', label: 'Has IDs + Claims' },
                        { key: 'no_claims', label: 'Has IDs, No Claims' },
                        { key: 'no_ids', label: 'No OnchainIDs' },
                      ] as { key: DemoScenario; label: string }[]).map((opt) => (
                        <button
                          key={opt.key}
                          onClick={() => setDemoScenario(opt.key)}
                          className={`rounded-md px-3 py-1.5 text-xs text-left transition-colors ${
                            demoScenario === opt.key
                              ? 'bg-orange-200 text-orange-900 font-medium'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Current Compliance Info */}
                  <div className="border-t border-orange-200 pt-3">
                    <p className="text-xs font-medium mb-2">Current State:</p>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div>Detected IDs: <span className="font-mono">{detectedIds.length}</span></div>
                      <div>Total Claims: <span className="font-mono">{detectedIds.reduce((sum, oid) => sum + oid.claims.length, 0)}</span></div>
                      <div>Requirements: <span className="font-mono">{requirements.length}</span></div>
                      <div>
                        Status:{' '}
                        <span className={`font-medium ${allChecksPassed ? 'text-green-600' : hasIssues ? 'text-amber-600' : 'text-blue-600'}`}>
                          {isDetecting || isChecking ? 'Checking...' : allChecksPassed ? 'Passed' : 'Issues Detected'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Compliance Pre-Check ───────────────────────────────────────────────────────

function CompliancePreCheck({
  isDetecting,
  isChecking,
  steps,
  allPassed,
  hasIssues,
  showDetail,
  onToggleDetail,
}: {
  isDetecting: boolean;
  isChecking: boolean;
  steps: ComplianceSteps | null;
  allPassed: boolean;
  hasIssues: boolean;
  showDetail: boolean;
  onToggleDetail: () => void;
}) {
  const loading = isDetecting || isChecking;

  return (
    <Card className={`${hasIssues ? 'border-amber-500' : allPassed ? 'border-green-500' : 'border-blue-500'}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {loading ? (
              <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-5 w-5 animate-spin text-blue-500" />
            ) : allPassed ? (
              <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-green-500" />
            ) : hasIssues ? (
              <Icon name="streamline:warning-triangle" className="h-5 w-5 text-amber-500" />
            ) : (
              <Icon name="streamline:interface-alert-information-circle-information-frame-info-more-help-point-circle" className="h-5 w-5 text-blue-500" />
            )}
            <span className="font-medium text-sm">
              {loading ? 'Checking compliance...' : allPassed ? 'Compliance Pre-Check Passed' : 'Compliance Issues Detected'}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={onToggleDetail}>
            {showDetail ? <Icon name="streamline:interface-arrows-button-up-arrow-up-keyboard" className="h-4 w-4" /> : <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-4 w-4" />}
          </Button>
        </div>

        {showDetail && steps && (
          <div className="mt-3 space-y-2">
            <ComplianceStepRow
              label="Ascend OnchainID"
              status={steps.onchainId.status}
              message={steps.onchainId.message}
            />
            <ComplianceStepRow
              label="Required claim topics"
              status={steps.claim.status}
              message={steps.claim.message}
            />
            <ComplianceStepRow
              label="Compliance rules"
              status={steps.policy.status}
              message={
                steps.policy.status === 'pass'
                  ? 'Runtime predicates evaluated at borrow time'
                  : steps.policy.message
              }
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ComplianceStepRow({
  label,
  status,
  message,
  detail,
}: {
  label: string;
  status: ComplianceStepStatus;
  message: string;
  detail?: string;
}) {
  const icon =
    status === 'checking' ? <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-3.5 w-3.5 animate-spin text-blue-500" /> :
    status === 'pass' ? <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3.5 w-3.5 text-green-500" /> :
    status === 'not-required' ? <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3.5 w-3.5 text-gray-400" /> :
    <Icon name="streamline:interface-delete-circle-button-delete-remove-add-circle-buttons" className="h-3.5 w-3.5 text-red-500" />;

  return (
    <div className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
          <span className={`text-xs ${status === 'pass' || status === 'not-required' ? 'text-green-700' : status === 'fail' ? 'text-red-700' : 'text-blue-700'}`}>
            {message}
          </span>
        </div>
        {detail && <p className="text-[10px] text-muted-foreground mt-0.5">{detail}</p>}
      </div>
    </div>
  );
}

// ─── OnchainID Section ──────────────────────────────────────────────────────────

function OnchainIdSection({
  ids,
  isDetecting,
  onCreateId,
}: {
  ids: OnchainId[];
  isDetecting: boolean;
  onCreateId?: () => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  function handleCreate() {
    if (!onCreateId) return;
    setIsCreating(true);
    setTimeout(() => {
      onCreateId();
      setIsCreating(false);
    }, 2000);
  }

  if (isDetecting) {
    return (
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-5 w-5 animate-spin text-blue-600" />
            <p className="text-sm text-blue-900">Detecting OnchainIDs across identity registries...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (ids.length === 0) {
    return (
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Icon name="streamline:warning-triangle" className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-900">No OnchainID Detected</p>
              <p className="text-xs text-yellow-700 mt-1">
                No identity was found for your wallet address across Ascend Identity Registry, ERC-3643 Token Registry, or other whitelisted registries. You need an OnchainID with the required claims to access this facility.
              </p>
            </div>
          </div>
          {onCreateId && (
            <div className="rounded-lg border border-yellow-300 bg-white/60 p-4 space-y-3">
              <div className="flex items-start gap-2.5">
                <Icon name="streamline:fingerprint-2" className="h-4 w-4 text-yellow-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Create an OnchainID</p>
                  <p className="text-xs text-yellow-700 mt-0.5">
                    Mint a new OnchainID on the Ascend Identity Registry. This will deploy an identity contract
                    linked to your wallet address. You can then attach claims (KYC, accreditation, etc.) to it.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                disabled={isCreating}
                onClick={handleCreate}
              >
                {isCreating ? (
                  <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-1.5 animate-spin" /> Creating OnchainID...</>
                ) : (
                  <><Icon name="streamline:fingerprint-2" className="h-4 w-4 mr-1.5" /> Create OnchainID on Ascend Registry</>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const SOURCE_LABELS: Record<string, string> = {
    ascend: 'Ascend Identity Registry',
    external: 'ERC-3643 Token Registry',
    erc3643: 'ERC-3643 Token Registry',
  };

  return (
    <Card>
      <CardHeader className="p-6">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon name="streamline:fingerprint-2" className="h-4 w-4" />
          Your Ascend OnchainID
        </CardTitle>
        <CardDescription>
          {ids.length === 0
            ? 'No OnchainID found. Create one to use this facility.'
            : `${ids[0].claims.length} claim${ids[0].claims.length === 1 ? '' : 's'} attached`}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-3">
        {ids.map((oid) => (
          <div key={oid.id} className="rounded-lg border border-border">
            <button
              onClick={() => toggle(oid.id)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Icon name="streamline:fingerprint-2" className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium font-mono truncate">{oid.address}</p>
                  <p className="text-xs text-muted-foreground">
                    {SOURCE_LABELS[oid.source] ?? oid.source} · {oid.claims.length} claim{oid.claims.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                  oid.claims.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {oid.claims.length > 0 ? 'Has Claims' : 'No Claims'}
                </span>
                {expanded.has(oid.id) ? <Icon name="streamline:interface-arrows-button-up-arrow-up-keyboard" className="h-4 w-4 text-muted-foreground" /> : <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-4 w-4 text-muted-foreground" />}
              </div>
            </button>
            {expanded.has(oid.id) && (
              <div className="border-t border-border px-4 py-3 space-y-2">
                {oid.claims.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No claims attached to this identity.</p>
                ) : (
                  oid.claims.map((claim, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs rounded-md bg-muted/50 px-3 py-2">
                      <div>
                        <span className="font-medium uppercase">{claim.type.replace(/_/g, ' ')}</span>
                        <span className="text-muted-foreground ml-2">by {claim.issuer}</span>
                      </div>
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                        {claim.value}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── Compliance Requirements Card ───────────────────────────────────────────────

function ComplianceRequirementsCard({
  requirements,
  result,
  isConnected,
  facilityName,
  kycSubmitted,
  claimRequested,
  onSubmitKyc,
  onRequestClaim,
}: {
  requirements: ComplianceRequirement[];
  result: ComplianceCheckResult | null;
  isConnected: boolean;
  facilityName: string;
  kycSubmitted: boolean;
  claimRequested: boolean;
  onSubmitKyc: () => void;
  onRequestClaim: () => void;
}) {
  return (
    <Card>
      <CardHeader className="p-6">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon name="streamline:shield-check" className="h-4 w-4" />
          Compliance Requirements
        </CardTitle>
        <CardDescription>
          Claims and verifications required by {facilityName} to create a margin account.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-3">
        {requirements.map((req, idx) => {
          const isMet = result?.requirementsMet.includes(req.claimType) ?? false;
          const isKyc = req.type === 'external_kyc_claim';

          return (
            <div key={idx} className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div className="flex items-start gap-3">
                {isMet ? (
                  <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <Icon name="streamline:interface-delete-circle-button-delete-remove-add-circle-buttons" className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="text-sm font-medium">{req.description}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Claim type: <span className="font-mono">{req.claimType.replace(/_/g, ' ')}</span>
                    <span className="mx-1.5">·</span>
                    Source: {isKyc ? 'External KYC Provider' : 'Internal Verification'}
                  </p>
                </div>
              </div>
              {isConnected && !isMet && (
                <div className="flex-shrink-0">
                  {isKyc ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={kycSubmitted}
                      onClick={onSubmitKyc}
                    >
                      {kycSubmitted ? (
                        <><Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-3.5 w-3.5 mr-1.5" /> Pending</>
                      ) : (
                        <>Submit KYC</>
                      )}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={claimRequested}
                      onClick={onRequestClaim}
                    >
                      {claimRequested ? (
                        <><Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-3.5 w-3.5 mr-1.5" /> Requested</>
                      ) : (
                        <>Request Claim</>
                      )}
                    </Button>
                  )}
                </div>
              )}
              {isMet && (
                <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 flex-shrink-0">
                  Verified
                </span>
              )}
            </div>
          );
        })}

        {isConnected && result && result.requirementsMet.length > 0 && result.requirementsNeeded.length > 0 && (
          <Alert>
            <AlertDescription className="text-xs">
              Some requirements are met but others still need verification. Complete all requirements to gain full access.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, icon }: { title: string; value: string; subtitle?: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}

function ParamItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div>
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className="text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
}

function ExpandableSection({
  title,
  description,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  description: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border">
      <button onClick={onToggle} className="flex w-full items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors">
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {isOpen ? <Icon name="streamline:interface-arrows-button-up-arrow-up-keyboard" className="h-5 w-5 text-muted-foreground flex-shrink-0" /> : <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
      </button>
      {isOpen && <div className="border-t border-border px-5 py-5 sm:px-6">{children}</div>}
    </div>
  );
}
